import { capitalize } from 'lodash';

export type GraphqlErrorExtensions = {
  requestId?: string;
};

export interface IError {
  key?: string;
  message?: string;
  details?: string;
  value?: unknown;
  extensions?: GraphqlErrorExtensions;
}

export interface MutationError {
  graphQLErrors: IGraphQLError[];
}

export class GraphQLMutationError extends Error implements MutationError {
  public graphQLErrors: IGraphQLError[] = [];

  constructor(key: string, message: string) {
    super();
    this.graphQLErrors = [{ key, message }];
  }
}

interface IGraphQLError {
  key: string;
  message: string;
  details?: string;
  value?: unknown;
  validationErrors?: IError;
  extensions?: GraphqlErrorExtensions;
}

export enum RangeErrorKind {
  MIN = 'min',
  MAX = 'max',
}

// Text for the mapped errors.
enum ERRORS {
  PASSWORD_INCORRECT = 'Wrong password',
  PASSWORD_SHORT = 'Password needs too be minimum 8 characters long.',
  PASSWORD_SIMPLE = 'Password needs too have at least 3/4 from following: uppercase letters, lowercase letters, numbers or special characters.',
  URL_SLUG_TAKEN = 'The url name has already been taken.',
  NAME_TAKEN = 'This name has already been taken.',
  ACTIVE_ACCOUNTS = "Can't be terminated while there are ACTIVE accounts connected to it.",
  NAME_MISMATCH = "Name doesn't match.",
}

// Map for the validation errors got from the backend.
export const ERRORS_MAP = {
  password: {
    incorrect_password: ERRORS.PASSWORD_INCORRECT,
    password_short: ERRORS.PASSWORD_SHORT,
    password_simple: ERRORS.PASSWORD_SIMPLE,
  },
  url_slug: {
    'has already been taken': ERRORS.URL_SLUG_TAKEN,
  },
  name: {
    'has already been taken': ERRORS.NAME_TAKEN,
    mismatch: ERRORS.NAME_MISMATCH,
  },
  required: 'This field is required',
  negative: "can't be negative",
  active_accounts: {
    active_accounts: ERRORS.ACTIVE_ACCOUNTS,
  },
};

/**
 * Parses the network validation error and returns array of IError objects.
 * @param error
 */
export function getGraphQLErrors(error: string | Error): IError[] {
  const formattedError: MutationError = JSON.parse(JSON.stringify(error));
  let errors: IError[] = [];
  if (formattedError.graphQLErrors && formattedError.graphQLErrors.length > 0) {
    errors = formattedError.graphQLErrors.map(e => {
      if (e.validationErrors) {
        return e.validationErrors;
      }

      return {
        key: e.key || e.message,
        message: typeof e.message === 'string' ? e.message : e.message[0],
        details: e.details,
        value: e.value,
        extensions: e.extensions,
      };
    });
    return errors;
  }
  // Returns empty array if there are no errors.
  return errors;
}

/**
 * Reduces result of getGraphQLErrors to the single errors object
 * with { [fieldName]: errorMessage } structure
 * similar to what Formik uses
 * @param error
 */
export function getGraphQLErrorsObject(error: string): Record<string, string> {
  return getGraphQLErrors(error).reduce(
    (acc, e) => ({
      ...acc,
      [e.key as string]:
        e.message && e.details
          ? e.message.concat(`: ${e.details}`)
          : e.message ?? capitalize(e.key?.replace(/_/gi, ' ')),
    }),
    {}
  ) as Record<string, string>;
}

/**
 * Extracts the error message for the given field key and possible errors array.
 * @param key
 * @param errors
 */
export function getErrorMessage(key: string, errors: IError[]): string | undefined {
  if (key && errors && errors.length > 0) {
    const error = errors.find(e => e.key === key);
    return error ? ERRORS_MAP[key][error.message] : undefined;
  }
  return undefined;
}

/**
 * Extracts the error object for the given key.
 * @param key
 * @param errors
 */
export function getError(key: string, errors: IError[]) {
  if (key && errors && errors.length > 0) {
    return errors.find(e => e.key === key);
  }
  return undefined;
}

/**
 * Get error message for minimum and maximum.
 * @param value
 * @param kind
 */
export function getRangeError(value: number, kind: RangeErrorKind) {
  if (kind === RangeErrorKind.MAX) {
    return `can't be bigger than ${value}`;
  }
  return `can't be less than ${value}`;
}

/**
 * Get error message for string length.
 * @param value
 * @param kind
 */
export function getStringLengthError(value: number, kind: RangeErrorKind) {
  if (kind === RangeErrorKind.MAX) {
    return `Maximum length of the field is ${value} characters.`;
  }
  return `Minimum length of the field is ${value} characters.`;
}

export function getFirstGraphqlErrorMessage(error: string | Error) {
  const errors = getGraphQLErrors(error);
  if (errors.length === 0) {
    return undefined;
  }
  const firstError = errors[0];
  if (firstError.details) {
    return firstError.message?.concat(`: ${firstError.details}`);
  }
  return firstError.message;
}
